/**
 * Electoral metadata API client (fields, grid, values, import).
 */
import { apiClient } from "../client";
import type {
  ElectoralMetadataFieldListItem,
  CreateElectoralMetadataFieldRequest,
  CreateElectoralMetadataFieldResponse,
  UpdateElectoralMetadataFieldRequest,
  UpsertElectoralMetadataValueRequest,
  UpsertElectoralMetadataValuesBatchRequest,
  MunicipalityMetadataGridDto,
  ImportValidationReportDto
} from "./types";

const FIELDS_BASE = "electoral-metadata/fields";
const MUNICIPALITIES_BASE = "electoral-metadata/municipalities";

/**
 * Check if current user has access to manage field definitions (RiksAdmin or higher). Returns true if 204, false on 403.
 */
export async function checkElectoralMetadataFieldsAccess(): Promise<boolean> {
  try {
    await apiClient.get(`${FIELDS_BASE}/access`);
    return true;
  } catch {
    return false;
  }
}
const VALUES_BASE = "electoral-metadata/values";
const IMPORT_BASE = "electoral-metadata/import";

export async function fetchElectoralMetadataFields(): Promise<
  ElectoralMetadataFieldListItem[]
> {
  const { data } = await apiClient.get<ElectoralMetadataFieldListItem[]>(
    FIELDS_BASE
  );
  return data;
}

export async function createElectoralMetadataField(
  request: CreateElectoralMetadataFieldRequest
): Promise<CreateElectoralMetadataFieldResponse> {
  const { data } = await apiClient.post<CreateElectoralMetadataFieldResponse>(
    FIELDS_BASE,
    { name: request.name, type: request.type }
  );
  return data;
}

export async function updateElectoralMetadataField(
  fieldId: string,
  request: UpdateElectoralMetadataFieldRequest
): Promise<void> {
  await apiClient.put(`${FIELDS_BASE}/${fieldId}`, {
    name: request.name,
    type: request.type
  });
}

export async function deleteElectoralMetadataField(
  fieldId: string
): Promise<void> {
  await apiClient.delete(`${FIELDS_BASE}/${fieldId}`);
}

export async function reorderElectoralMetadataFields(
  orderedFieldIds: string[]
): Promise<void> {
  await apiClient.put(`${FIELDS_BASE}/reorder`, {
    orderedFieldIds
  });
}

export async function checkMunicipalityMetadataCanEdit(
  municipalityId: string
): Promise<boolean> {
  try {
    const { data } = await apiClient.get<{ canEdit: boolean }>(
      `${MUNICIPALITIES_BASE}/${municipalityId}/can-edit`
    );
    return data?.canEdit ?? false;
  } catch {
    return false;
  }
}

export async function fetchMunicipalityMetadataGrid(
  municipalityId: string
): Promise<MunicipalityMetadataGridDto | null> {
  try {
    const { data } = await apiClient.get<MunicipalityMetadataGridDto>(
      `${MUNICIPALITIES_BASE}/${municipalityId}`
    );
    return data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function upsertElectoralMetadataValue(
  request: UpsertElectoralMetadataValueRequest
): Promise<void> {
  await apiClient.put(VALUES_BASE, {
    electoralDistrictId: request.electoralDistrictId,
    fieldDefinitionId: request.fieldDefinitionId,
    value: request.value
  });
}

export async function upsertElectoralMetadataValuesBatch(
  request: UpsertElectoralMetadataValuesBatchRequest
): Promise<void> {
  await apiClient.put(`${VALUES_BASE}/batch`, {
    values: request.values.map((v) => ({
      electoralDistrictId: v.electoralDistrictId,
      fieldDefinitionId: v.fieldDefinitionId,
      value: v.value
    }))
  });
}

export interface ImportSuccessResponse {
  rowsImported: number;
  cellsUpdated: number;
}

export async function importElectoralMetadataCsv(
  municipalityId: string,
  file: File
): Promise<
  | { success: true; rowsImported: number; cellsUpdated: number }
  | { success: false; report: ImportValidationReportDto }
> {
  const formData = new FormData();
  formData.append("municipalityId", municipalityId);
  formData.append("file", file);

  try {
    const { data } = await apiClient.post<ImportSuccessResponse>(IMPORT_BASE, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return {
      success: true,
      rowsImported: data?.rowsImported ?? 0,
      cellsUpdated: data?.cellsUpdated ?? 0
    };
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: ImportValidationReportDto } };
    if (axiosErr.response?.status === 422 && axiosErr.response.data) {
      return { success: false, report: axiosErr.response.data };
    }
    throw err;
  }
}
